import { useEffect, useState } from "react";
import { Button, Divider, Grid, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { AppRoutes } from "../../common/routesUtils";
import FullScreenDialog from "../../components/common/FullScreenDialog";
import FormPasswordField from "../../components/forms/FormPasswordField";
import FormSwitchField from "../../components/forms/FormSwitchField";
import FormTextField from "../../components/forms/FormTextField";
import { PublicView } from "../../components/layout/PublicView";
import { SignInRequest } from "../../datasources/requests/auth";
import { SignInWorkflowState, useAuthentication } from "../../hooks/useAuthentication";
import CurrentUserPassword from "./components/CurrentUserPassword";
import RecoverPassword from "./components/RecoverPassword";
import UserWorkspaceSelector from "./components/UserWorkspaceSelector";

const formWidth = 500;

/**
 * SignIn renders the sign in section of this application
 *
 * @returns the sign in view
 */
const SignIn = () => {
	const { t } = useTranslation("authentication");
	const navigateTo = useNavigate();
	const [canSubmit, setCanSubmit] = useState<boolean>(false);
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [showUpdatePassword, setShowUpdatePassword] = useState<boolean>(false);
	const [showSelectWorkspace, setShowSelectWorkspace] = useState<boolean>(false);
	const [showRecoverPassword, setShowRecoverPassword] = useState<boolean>(false);

	const {
		isAuthenticatingUser,
		authenticationError,
		availableWorkspaces,
		state,
		requireTosAcceptance,
		signIn,
		resetErrors,
		signOut,
		initiateRecoverPassword,
	} = useAuthentication();

	// Form management.
	const form = useForm<SignInRequest>({
		defaultValues: { username: "", password: "", tosAccepted: false },
	});

	const { resetField, watch } = form;

	const username = watch("username");
	const password = watch("password");

	const handleSignIn = form.handleSubmit((payload: SignInRequest) => {
		setShowPassword(false);
		signIn(payload);
	});

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	const resetForm = () => {
		resetField("password");
		resetErrors();
	};

	const onCancelSelection = () => {
		signOut();
		resetField("password");
		setShowPassword(false);
	};

	const cancelRecoverPassword = () => {
		setShowRecoverPassword(false);
	};

	useEffect(() => {
		if (!username || !password) {
			setCanSubmit(false);
		} else {
			setCanSubmit(username.trim() !== "" && password.trim() !== "");
		}
	}, [username, password]);

	useEffect(() => {
		setShowSelectWorkspace(state === SignInWorkflowState.SelectUserWorkspace);
		setShowUpdatePassword(state === SignInWorkflowState.UpdatePassword);
	}, [state]);

	if (state === SignInWorkflowState.Authenticated) {
		return <Navigate to={AppRoutes.PostSignInHome} />;
	}

	return (
		<PublicView errors={[authenticationError]} redirectOnUnauthorized={false} onAfterDisplayError={resetForm}>
			<Stack display="flex" justifyContent="center" alignItems="center">
				<Typography variant="h5" py={5}>
					{t("sign into greenrouter")}
				</Typography>

				<Grid container spacing={2} direction="column" sx={{ width: formWidth }}>
					<Grid>
						<FormTextField
							form={form}
							name="username"
							fullWidth
							autoComplete="off"
							disabled={isAuthenticatingUser}
							label={t("username", { context: "label" })}
							placeholder={t("username", { context: "placeholder" })}
						/>
					</Grid>
					<Grid>
						<FormPasswordField
							form={form}
							name="password"
							size="small"
							fullWidth
							autoComplete="off"
							disabled={isAuthenticatingUser}
							label={t("password", { context: "label" })}
							showPassword={showPassword}
							onToggleShowPassword={togglePasswordVisibility}
						/>
					</Grid>
					{requireTosAcceptance && (
						<Grid>
							<FormSwitchField form={form} name="tosAccepted" label={t("I accept terms of services")} />
						</Grid>
					)}
					<Grid textAlign="end">
						<Button
							className="confirm"
							loading={isAuthenticatingUser}
							loadingPosition="start"
							disabled={!canSubmit}
							onClick={handleSignIn}
						>
							{t("sign in")}
						</Button>
					</Grid>

					<Divider sx={{ pt: 3 }} />
					<Button variant="text" onClick={() => setShowRecoverPassword(true)}>
						{t("recover password", { context: "button" })}
					</Button>
				</Grid>
			</Stack>

			<FullScreenDialog
				isOpen={showSelectWorkspace}
				isLoading={isAuthenticatingUser}
				onClose={signOut}
				title={t("select your workspace")}
			>
				<Grid justifyContent="center">
					<UserWorkspaceSelector
						onCancel={onCancelSelection}
						onWorkspaceSelected={() => navigateTo(AppRoutes.PostSignInHome)}
						workspaces={availableWorkspaces}
					/>
				</Grid>
			</FullScreenDialog>

			<FullScreenDialog
				isOpen={showUpdatePassword}
				isLoading={isAuthenticatingUser}
				onClose={signOut}
				title={t("password expired, must be updated")}
			>
				<Grid container spacing={2} direction="column" justifyContent="center" sx={{ pt: 2 }}>
					<CurrentUserPassword onSuccess={signOut} />
				</Grid>
			</FullScreenDialog>

			<RecoverPassword
				open={showRecoverPassword}
				onCancel={cancelRecoverPassword}
				onConfirm={initiateRecoverPassword}
			/>
		</PublicView>
	);
};

export default SignIn;
